"use client"

import { useState, useEffect, useCallback } from "react"
import { useParams, useRouter, usePathname } from "next/navigation"
import { motion, AnimatePresence } from "framer-motion"
import { Section } from "@/components/layout/Section"
import { Container } from "@/components/layout/Container"
import { getReferenceBySlug, getReferenceById, getReferences } from "@/lib/cms/queries"
import type { Reference } from "@/types/database"
import Image from "next/image"
import Link from "next/link"
import { ArrowLeft, ExternalLink, Calendar, Tag, User, Link as LinkIcon, Image as ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { CTA } from "@/components/sections/CTA"
import useEmblaCarousel from "embla-carousel-react"
import { isValidLocale, type Locale } from "@/lib/i18n"

export default function ReferenceDetailPage() {
  const params = useParams()
  const router = useRouter()
  const pathname = usePathname()
  
  // Get current locale from pathname
  const getCurrentLocale = (): Locale => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const currentLocale = getCurrentLocale()
  const [reference, setReference] = useState<Reference | null>(null)
  const [allReferences, setAllReferences] = useState<Reference[]>([])
  const [loading, setLoading] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  
  // Embla Carousel for mobile
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: false,
    align: "start",
    slidesToScroll: 1,
    breakpoints: {
      '(min-width: 768px)': { active: false }, // Disable on desktop
    },
  })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback((emblaApi: any) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  useEffect(() => {
    if (params.id) {
      loadReference(params.id as string)
    }
  }, [params.id, currentLocale])

  const loadReference = async (slugOrId: string) => {
    try {
      // Tüm referansları yükle (önceki/sonraki için) - locale filtresi ile
      const allRefs = await getReferences('published', currentLocale)
      setAllReferences(allRefs)
      
      // Mevcut referansı yükle - önce slug ile dene (locale filtresi ile)
      let currentRef: Reference | null = null
      try {
        currentRef = await getReferenceBySlug(slugOrId, currentLocale)
      } catch (slugError) {
        // Slug ile bulunamazsa ID ile dene (locale filtresi ile)
        try {
          currentRef = await getReferenceById(slugOrId, currentLocale)
        } catch (idError) {
          throw new Error(currentLocale === 'tr' ? "Referans bulunamadı" : "Reference not found")
        }
      }
      
      setReference(currentRef)
    } catch (error) {
      console.error("Error loading reference:", error)
    } finally {
      setLoading(false)
    }
  }
  
  // Önceki ve sonraki referansları bul
  const getPreviousAndNext = () => {
    if (!reference || allReferences.length === 0) {
      return { previous: null, next: null }
    }
    
    const currentIndex = allReferences.findIndex(ref => ref.id === reference.id)
    
    if (currentIndex === -1) {
      return { previous: null, next: null }
    }
    
    const previous = currentIndex > 0 ? allReferences[currentIndex - 1] : null
    const next = currentIndex < allReferences.length - 1 ? allReferences[currentIndex + 1] : null
    
    return { previous, next }
  }
  
  const { previous, next } = getPreviousAndNext()

  // Calculate allImages - always defined, even if reference is null
  // Filter out empty strings and null/undefined values
  const allImages = reference ? [reference.image, ...(reference.additional_images || [])].filter((img): img is string => Boolean(img) && img.trim() !== "") : []

  // Keyboard navigation for lightbox - must be called before any early returns
  useEffect(() => {
    if (!lightboxOpen || allImages.length === 0) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false)
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev - 1 + allImages.length) % allImages.length)
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev + 1) % allImages.length)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, allImages.length])

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-slate-500">{currentLocale === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
      </div>
    )
  }

  if (!reference) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">
            {currentLocale === 'tr' ? 'Referans Bulunamadı' : 'Reference Not Found'}
          </h1>
          <Button asChild>
            <Link href={`/${currentLocale}/${currentLocale === 'tr' ? 'referanslar' : 'references'}`}>
              {currentLocale === 'tr' ? 'Referanslara Dön' : 'Back to References'}
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
  }

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % allImages.length)
  }

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + allImages.length) % allImages.length)
  }

  const referencesPath = currentLocale === 'tr' ? 'referanslar' : 'references'

  return (
    <div className="min-h-screen bg-white">
      <Section className="py-8 sm:py-12">
        <Container>
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => router.back()}
            className="mb-8"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {currentLocale === 'tr' ? 'Geri Dön' : 'Go Back'}
          </Button>

          {/* Meta Information - Ana Görselin Üstünde */}
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {reference.client_name && (
              <div className="flex items-center gap-2 p-4 bg-slate-50 rounded-lg">
                <User className="h-5 w-5 text-slate-600" />
                <div>
                  <div className="text-xs text-slate-500">{currentLocale === 'tr' ? 'Müşteri' : 'Client'}</div>
                  <div className="font-medium text-slate-900">{reference.client_name}</div>
                </div>
              </div>
            )}
            {reference.completion_date && (
              <div className="flex items-center gap-2 p-4 bg-slate-50 rounded-lg">
                <Calendar className="h-5 w-5 text-slate-600" />
                <div>
                  <div className="text-xs text-slate-500">{currentLocale === 'tr' ? 'Tamamlanma' : 'Completion'}</div>
                  <div className="font-medium text-slate-900">
                    {new Date(reference.completion_date).toLocaleDateString(
                      currentLocale === 'tr' ? 'tr-TR' : 'en-US',
                      { year: 'numeric', month: 'long' }
                    )}
                  </div>
                </div>
              </div>
            )}
            {reference.project_url && (
              <div className="flex items-center gap-2 p-4 bg-slate-50 rounded-lg">
                <LinkIcon className="h-5 w-5 text-slate-600" />
                <div>
                  <div className="text-xs text-slate-500">{currentLocale === 'tr' ? 'Proje Linki' : 'Project Link'}</div>
                  <a
                    href={reference.project_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-[rgb(3,74,166)] hover:underline flex items-center gap-1"
                  >
                    {currentLocale === 'tr' ? 'Ziyaret Et' : 'Visit'}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            )}
            {reference.tags && reference.tags.length > 0 && (
              <div className="flex items-center gap-2 p-4 bg-slate-50 rounded-lg">
                <Tag className="h-5 w-5 text-slate-600" />
                <div>
                  <div className="text-xs text-slate-500">{currentLocale === 'tr' ? 'Etiketler' : 'Tags'}</div>
                  <div className="font-medium text-slate-900">
                    {reference.tags.length} {currentLocale === 'tr' ? 'etiket' : 'tags'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Ana Görsel */}
          <div className="mb-8">
            <div 
              className="relative aspect-video w-full overflow-hidden rounded-xl bg-slate-100 cursor-pointer group"
              onClick={() => reference.image && reference.image.trim() !== "" && openLightbox(0)}
              onTouchEnd={() => reference.image && reference.image.trim() !== "" && openLightbox(0)}
            >
              {reference.image && reference.image.trim() !== "" ? (() => {
                const isVideo = /\.(webm|mp4|mov|avi|mkv)$/i.test(reference.image);
                if (isVideo) {
                  return (
                    <video
                      src={reference.image}
                      className="w-full h-full object-cover"
                      controls
                      preload="none"
                    />
                  );
                }
                return (
                  <>
                    <Image
                      src={reference.image}
                      alt={reference.title}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                      sizes="100vw"
                      priority
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <ImageIcon className="h-12 w-12 text-white" />
                      </div>
                    </div>
                  </>
                );
              })() : (
                <div className="w-full h-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 flex items-center justify-center">
                  <div className="text-center p-4">
                    <div className="text-4xl mb-2">📷</div>
                    <p className="text-sm text-slate-500 font-medium">
                      {currentLocale === 'tr' ? 'Görsel Eklenecek' : 'Image to be added'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {reference.category && reference.category.length > 0 && (
                <>
                  {reference.category.map((cat, index) => (
                    <span key={index} className="inline-block px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                      {cat}
                    </span>
                  ))}
                </>
              )}
              {reference.featured && (
                <span className="inline-block px-3 py-1 bg-[rgb(3,74,166)] text-white rounded-full text-xs font-medium">
                  {currentLocale === 'tr' ? 'Öne Çıkan' : 'Featured'}
                </span>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-mono">
              {reference.title}
            </h1>
            {reference.description && (() => {
              // Check if description contains HTML tags
              const hasHTML = /<[a-z][\s\S]*>/i.test(reference.description)
              
              // If it's already HTML, use it directly
              // Otherwise, convert plain text to HTML
              let htmlContent = reference.description
              
              if (!hasHTML) {
                // Convert plain text to HTML paragraphs
                htmlContent = reference.description
                  .split('\n\n')
                  .filter(p => p.trim())
                  .map(p => `<p>${p.trim().replace(/\n/g, '<br>')}</p>`)
                  .join('')
              }
              
              return (
                <div 
                  className="text-lg text-slate-600 w-full rich-text-content"
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
              )
            })()}
          </div>

          {/* Tags */}
          {reference.tags && reference.tags.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4 font-mono">
                {currentLocale === 'tr' ? 'Etiketler' : 'Tags'}
              </h2>
              <div className="flex flex-wrap gap-2">
                {reference.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-slate-50 text-slate-600 rounded-full text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Ek Görseller Galeri - Container dışında, soldan container içinde başlayan */}
          {reference.additional_images && reference.additional_images.filter((img): img is string => Boolean(img) && img.trim() !== "").length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4 font-mono">
                {currentLocale === 'tr' ? 'Proje Görselleri' : 'Project Images'}
              </h2>
            </div>
          )}

        </Container>

        {/* Ek Görseller Galeri - Container dışında, soldan container içinde başlayan */}
        {reference.additional_images && reference.additional_images.filter((img): img is string => Boolean(img) && img.trim() !== "").length > 0 && (
          <div className="mb-0">
            {/* Desktop View - Grid Layout */}
            <div className="hidden md:block">
              <div className="relative -mx-4 sm:-mx-6 lg:-mx-8">
                <div className="pl-4 sm:pl-6 lg:pl-8 pr-0">
                  <div className="flex gap-4">
                    {reference.additional_images
                      .filter((img): img is string => Boolean(img) && img.trim() !== "")
                      .slice(0, 4)
                      .map((img, index) => {
                        const isVideo = /\.(webm|mp4|mov|avi|mkv)$/i.test(img);
                        const imageIndex = index + 1;
                        return (
                          <div 
                            key={index} 
                            className="relative flex-shrink-0 w-[27%] aspect-video overflow-hidden rounded-lg bg-slate-100 cursor-pointer group"
                            onClick={() => openLightbox(imageIndex)}
                          >
                            {isVideo ? (
                              <>
                                <video
                                  src={img}
                                  className="w-full h-full object-cover"
                                  controls
                                  muted
                                  preload="none"
                                />
                                <div 
                                  className="absolute inset-0 z-[1] pointer-events-none" 
                                  style={{ backgroundColor: 'rgba(3, 74, 166, 0.5)' }}
                                ></div>
                              </>
                            ) : (
                              <>
                                <div className="absolute inset-0 z-0">
                                  <Image
                                    src={img}
                                    alt={`${reference.title} - ${currentLocale === 'tr' ? 'Görsel' : 'Image'} ${index + 1}`}
                                    fill
                                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                                    sizes="(max-width: 1024px) 50vw, 25vw"
                                  />
                                </div>
                                <div 
                                  className="absolute inset-0 z-[5]" 
                                  style={{ backgroundColor: 'rgba(3, 74, 166, 0.5)' }}
                                ></div>
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center z-[2]">
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ImageIcon className="h-8 w-8 text-white" />
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile View - Carousel */}
            <div className="md:hidden">
              <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex gap-4">
                  {reference.additional_images
                    .filter((img): img is string => Boolean(img) && img.trim() !== "")
                    .map((img, index) => {
                      const isVideo = /\.(webm|mp4|mov|avi|mkv)$/i.test(img);
                      const imageIndex = index + 1;
                      return (
                        <div 
                          key={index} 
                          className="relative flex-[0_0_85%] min-w-0 aspect-video overflow-hidden rounded-lg bg-slate-100 cursor-pointer group"
                          onClick={() => openLightbox(imageIndex)}
                        >
                          {isVideo ? (
                            <>
                              <video
                                src={img}
                                className="w-full h-full object-cover"
                                controls
                                muted
                                preload="none"
                              />
                              <div 
                                className="absolute inset-0 z-[1] pointer-events-none" 
                                style={{ backgroundColor: 'rgba(3, 74, 166, 0.5)' }}
                              ></div>
                            </>
                          ) : (
                            <>
                              <div className="absolute inset-0 z-0">
                                <Image
                                  src={img}
                                  alt={`${reference.title} - ${currentLocale === 'tr' ? 'Görsel' : 'Image'} ${index + 1}`}
                                  fill
                                  className="object-cover transition-transform duration-300 group-active:scale-105"
                                  sizes="85vw"
                                />
                              </div>
                              <div 
                                className="absolute inset-0 z-[5]" 
                                style={{ backgroundColor: 'rgba(3, 74, 166, 0.5)' }}
                              ></div>
                              <div className="absolute inset-0 bg-black/0 group-active:bg-black/10 transition-colors flex items-center justify-center z-[2]">
                                <div className="opacity-0 group-active:opacity-100 transition-opacity">
                                  <ImageIcon className="h-8 w-8 text-white" />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
              
              {/* Pagination Dots */}
              {reference.additional_images.filter((img): img is string => Boolean(img) && img.trim() !== "").length > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                  {reference.additional_images
                    .filter((img): img is string => Boolean(img) && img.trim() !== "")
                    .map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          if (emblaApi) {
                            emblaApi.scrollTo(index)
                          }
                        }}
                        className={`transition-all duration-300 rounded-full touch-manipulation ${
                          selectedIndex === index
                            ? 'w-8 h-2 bg-[rgb(3,74,166)]'
                            : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'
                        }`}
                        aria-label={`${currentLocale === 'tr' ? 'Görsel' : 'Image'} ${index + 1}`}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Section>

      <Section className="pt-0 pb-8 sm:pb-12">
        <Container>
          {/* Previous/Next Navigation */}
          {(previous || next) && (
            <div className="pt-8 border-t border-slate-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                {previous ? (
                  <Link 
                    href={`/${currentLocale}/${referencesPath}/${previous.slug || previous.id}`}
                    className="flex-1 group"
                  >
                    <div className="flex items-center gap-4 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors">
                      <div className="flex-shrink-0">
                        <ChevronLeft className="h-5 w-5 text-slate-600 group-hover:text-[rgb(3,74,166)] transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-500 mb-1">
                          {currentLocale === 'tr' ? 'Önceki Proje' : 'Previous Project'}
                        </div>
                        <div className="font-semibold text-slate-900 truncate group-hover:text-[rgb(3,74,166)] transition-colors">
                          {previous.title}
                        </div>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}
                
                {next ? (
                  <Link 
                    href={`/${currentLocale}/${referencesPath}/${next.slug || next.id}`}
                    className="flex-1 group"
                  >
                    <div className="flex items-center gap-4 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors">
                      <div className="flex-1 min-w-0 text-right">
                        <div className="text-xs text-slate-500 mb-1">
                          {currentLocale === 'tr' ? 'Sonraki Proje' : 'Next Project'}
                        </div>
                        <div className="font-semibold text-slate-900 truncate group-hover:text-[rgb(3,74,166)] transition-colors">
                          {next.title}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <ChevronRight className="h-5 w-5 text-slate-600 group-hover:text-[rgb(3,74,166)] transition-colors" />
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            </div>
          )}

        </Container>
      </Section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && allImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
            onTouchEnd={(e) => {
              // Sadece backdrop'a dokunulursa kapat (görsel dışındaki alan)
              if (e.target === e.currentTarget) {
                closeLightbox()
              }
            }}
          >
            {/* Close Button */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                closeLightbox()
              }}
              onTouchEnd={(e) => {
                e.stopPropagation()
                closeLightbox()
              }}
              className="absolute top-20 right-4 sm:top-4 z-[110] p-3 sm:p-2 bg-white/20 hover:bg-white/30 active:bg-white/40 text-white rounded-full transition-colors touch-manipulation shadow-lg"
              aria-label={currentLocale === 'tr' ? 'Kapat' : 'Close'}
            >
              <X className="h-8 w-8 sm:h-6 sm:w-6" />
            </button>

            {/* Navigation Buttons */}
            {allImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    prevImage()
                  }}
                  className="absolute left-4 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                  aria-label={currentLocale === 'tr' ? 'Önceki' : 'Previous'}
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    nextImage()
                  }}
                  className="absolute right-4 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                  aria-label={currentLocale === 'tr' ? 'Sonraki' : 'Next'}
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}

            {/* Image Counter */}
            {allImages.length > 1 && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2 bg-white/10 text-white rounded-full text-sm">
                {lightboxIndex + 1} / {allImages.length}
              </div>
            )}

            {/* Image Container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative w-full h-full max-w-7xl max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              {(() => {
                const currentImage = allImages[lightboxIndex]
                
                // Safety check - should not happen due to filtering, but just in case
                if (!currentImage || currentImage.trim() === "") {
                  return (
                    <div className="w-full h-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 flex items-center justify-center">
                      <div className="text-center p-4">
                        <div className="text-4xl mb-2">📷</div>
                        <p className="text-sm text-slate-500 font-medium">
                          {currentLocale === 'tr' ? 'Görsel Bulunamadı' : 'Image Not Found'}
                        </p>
                      </div>
                    </div>
                  )
                }
                
                const isVideo = /\.(webm|mp4|mov|avi|mkv)$/i.test(currentImage)
                
                if (isVideo) {
                  return (
                    <video
                      src={currentImage}
                      className="max-w-full max-h-full object-contain"
                      controls
                      preload="none"
                    />
                  )
                }
                
                return (
                  <div className="relative w-full h-full max-w-7xl max-h-[90vh]">
                    <Image
                      src={currentImage}
                      alt={`${reference.title} - ${currentLocale === 'tr' ? 'Görsel' : 'Image'} ${lightboxIndex + 1}`}
                      fill
                      className="object-contain"
                      sizes="100vw"
                      priority
                    />
                  </div>
                )
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <CTA locale={currentLocale} />
    </div>
  )
}

